import React, { useState, useEffect, useRef } from "react";
import { getEventDashboard, getCampaignNames } from "../api/Calling";
import "./Home.css";
import { BiTrendingUp } from "react-icons/bi";
import { FaFilter, FaSync, FaDownload, FaFilePdf, FaFileExcel, FaChevronDown } from "react-icons/fa";
import { MdRefresh } from "react-icons/md";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";

const toISTDateString = (isoString) => {
  if (!isoString) return "N/A";
  const date = new Date(isoString);
  const istOffset = 5.5 * 60 * 60 * 1000;
  const istDate = new Date(date.getTime() + istOffset);
  const day = String(istDate.getUTCDate()).padStart(2, "0");
  const month = String(istDate.getUTCMonth() + 1).padStart(2, "0");
  const year = istDate.getUTCFullYear();
  return `${day}-${month}-${year}`;
};

const Home = () => {
  const [tempFilters, setTempFilters] = useState({
    dateRangeType: "today",
    customStartDate: "",
    customEndDate: "",
    campaignFilter: "",
    sourceFilter: "",
  });

  const [appliedFilters, setAppliedFilters] = useState({
    dateRangeType: "today",
    customStartDate: "",
    customEndDate: "",
    campaignFilter: "",
    sourceFilter: "",
  });

  const [pidTableData, setPidTableData] = useState([]);
  const [organicTableData, setOrganicTableData] = useState([]);
  const [summary, setSummary] = useState({ total: 0, success: 0, fail: 0, organic: 0, pid: 0 });
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showDownloadMenu, setShowDownloadMenu] = useState(false);

  const downloadMenuRef = useRef(null);
  // Holds full [{ camp_name, p_id }] list from Whitelist API — avoids stale closure issues
  const campaignsRef = useRef([]);

  useEffect(() => {
    fetchCampaigns();
  }, []);

  useEffect(() => {
    // Only re-fetch on filter change if we already have campaign data
    if (campaignsRef.current.length > 0) {
      fetchDashboardData();
    }
  }, [appliedFilters]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (downloadMenuRef.current && !downloadMenuRef.current.contains(e.target)) {
        setShowDownloadMenu(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Fetch campaign+pid combos from Whitelist API, then immediately fetch dashboard
  const fetchCampaigns = async () => {
    try {
      const response = await getCampaignNames();
      if (response.status) {
        const campData = response.data || [];
        // campData = [{ camp_name: "P1", p_id: "pub_abc" }, ...]

        // Unique camp names for the filter dropdown
        const uniqueNames = [...new Set(campData.map((c) => c.camp_name))];
        setCampaigns(uniqueNames);

        // Store full combos in ref so fetchDashboardData always has latest value
        campaignsRef.current = campData;

        // Fetch dashboard immediately with the fresh combo list
        fetchDashboardData(campData);
      }
    } catch (err) {
      console.error("Error fetching campaigns:", err);
    }
  };

  const calculateDateRange = (filters) => {
    const today = new Date();
    const fmt = (d) => {
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, "0");
      const dd = String(d.getDate()).padStart(2, "0");
      return `${y}-${m}-${dd}`;
    };
    let startDate = "";
    let endDate = fmt(today);
    switch (filters.dateRangeType) {
      case "today": startDate = fmt(today); break;
      case "yesterday":
        const yest = new Date(today); yest.setDate(yest.getDate() - 1);
        startDate = fmt(yest); endDate = fmt(yest); break;
      case "last15":
        const l15 = new Date(today); l15.setDate(l15.getDate() - 15);
        startDate = fmt(l15); break;
      case "last30":
        const l30 = new Date(today); l30.setDate(l30.getDate() - 30);
        startDate = fmt(l30); break;
      case "last6months":
        const l6 = new Date(today); l6.setMonth(l6.getMonth() - 6);
        startDate = fmt(l6); break;
      case "custom":
        startDate = filters.customStartDate; endDate = filters.customEndDate; break;
      default: startDate = fmt(today);
    }
    return { startDate, endDate };
  };

  const buildQueryParams = (filters) => {
    const params = new URLSearchParams();
    const { startDate, endDate } = calculateDateRange(filters);
    if (startDate) params.append("startDate", startDate);
    if (endDate) params.append("endDate", endDate);
    if (filters.campaignFilter) params.append("camp_name", filters.campaignFilter);
    if (filters.sourceFilter) params.append("source", filters.sourceFilter);
    params.append("status", "success");
    return `?${params.toString()}`;
  };

  // campListOverride: passed directly from fetchCampaigns to avoid stale state
  const fetchDashboardData = async (campListOverride) => {
    setLoading(true);
    setError("");
    try {
      const response = await getEventDashboard(buildQueryParams(appliedFilters));
      // Use override if provided (called right after fetchCampaigns), else use ref
      const campList = campListOverride || campaignsRef.current;

      if (response.success) {
        const rawData = response.data || [];

        // ── Organic: aggregate as-is ─────────────────────────────────────────
        const organicGrouped = {};
        rawData.filter((e) => e.source === "organic").forEach((e) => {
          const key = `${e.camp_name || "N/A"}_${e.p_id || "-"}_${e.event || "N/A"}`;
          if (!organicGrouped[key]) {
            organicGrouped[key] = {
              camp_name: e.camp_name || "N/A",
              p_id: e.p_id || "-",
              event: e.event || "N/A",
              totalCount: 0,
            };
          }
          organicGrouped[key].totalCount++;
        });
        setOrganicTableData(Object.values(organicGrouped));

        // ── PID: group actual PID events from response by camp_name + p_id ──
        // Structure: { "camp_name__p_id__event": { camp_name, p_id, event, totalCount } }
        const pidGrouped = {};
        rawData.filter((e) => e.source === "pid").forEach((e) => {
          const key = `${e.camp_name || "N/A"}__${e.p_id || "-"}__${e.event || "N/A"}`;
          if (!pidGrouped[key]) {
            pidGrouped[key] = {
              camp_name: e.camp_name || "N/A",
              p_id: e.p_id || "-",
              event: e.event || "N/A",
              totalCount: 0,
            };
          }
          pidGrouped[key].totalCount++;
        });

        // ── Build PID table using Whitelist combos as master list ─────────────
        // Filter by selected campaign if any
        const relevantCombos = appliedFilters.campaignFilter
          ? campList.filter((c) => c.camp_name === appliedFilters.campaignFilter)
          : campList;

        const filledRows = [];
        relevantCombos.forEach((combo) => {
          // Find all events for this exact camp_name + p_id combo
          const matchKeys = Object.keys(pidGrouped).filter((k) =>
            k.startsWith(`${combo.camp_name}__${combo.p_id}__`)
          );

          if (matchKeys.length > 0) {
            // Has real PID event data → show actual rows with real counts
            matchKeys.forEach((k) => filledRows.push(pidGrouped[k]));
          } else {
            // No events for this combo in selected period → show 0 row
            filledRows.push({
              camp_name: combo.camp_name,
              p_id: combo.p_id,
              event: "af_subscribe",
              totalCount: 0,
            });
          }
        });

        setPidTableData(filledRows);
        setSummary(response.summary || { total: 0, success: 0, fail: 0, organic: 0, pid: 0 });

      } else {
        setError(response.message || "Failed to fetch data");
        const relevantCombosErr = appliedFilters.campaignFilter
          ? campList.filter((c) => c.camp_name === appliedFilters.campaignFilter)
          : campList;
        setPidTableData(
          relevantCombosErr.map((c) => ({
            camp_name: c.camp_name,
            p_id: c.p_id,
            event: "af_subscribe",
            totalCount: 0,
          }))
        );
        setOrganicTableData([]);
        setSummary({ total: 0, success: 0, fail: 0, organic: 0, pid: 0 });
      }
    } catch (err) {
      setError("Network error. Please try again.");
      console.error(err);
      setPidTableData([]);
      setOrganicTableData([]);
      setSummary({ total: 0, success: 0, fail: 0, organic: 0, pid: 0 });
    } finally {
      setLoading(false);
    }
  };

  // ─── PDF ─────────────────────────────────────────────────────────────────────
  const handleDownloadPDF = () => {
    setShowDownloadMenu(false);
    const doc = new jsPDF({ orientation: "landscape" });
    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.text("Event Tracker Dashboard – PID Report", 14, 15);
    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    doc.text(`Generated: ${toISTDateString(new Date().toISOString())} (IST)`, 14, 22);
    autoTable(doc, {
      startY: 28,
      head: [["Campaign Name", "PID", "Event", "Total Count"]],
      body: pidTableData.map((r) => [r.camp_name, r.p_id, r.event, r.totalCount]),
      headStyles: { fillColor: [37, 99, 235], textColor: 255, fontStyle: "bold", halign: "center", fontSize: 9 },
      bodyStyles: { fontSize: 9 },
      columnStyles: {
        0: { cellWidth: 70 },
        1: { halign: "center", cellWidth: 45 },
        2: { halign: "center", cellWidth: 50 },
        3: { halign: "center", cellWidth: 30 },
      },
      alternateRowStyles: { fillColor: [239, 246, 255] },
      margin: { left: 14, right: 14 },
    });
    doc.save(`PID_Report_${toISTDateString(new Date().toISOString())}.pdf`);
  };

  // ─── Excel ───────────────────────────────────────────────────────────────────
  const handleDownloadExcel = () => {
    setShowDownloadMenu(false);
    const wsData = [
      ["Campaign Name", "PID", "Event", "Total Count"],
      ...pidTableData.map((r) => [r.camp_name, r.p_id, r.event, r.totalCount]),
    ];
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(wsData);
    ws["!cols"] = [{ wch: 30 }, { wch: 20 }, { wch: 20 }, { wch: 14 }];
    XLSX.utils.book_append_sheet(wb, ws, "PID Report");
    XLSX.writeFile(wb, `PID_Report_${toISTDateString(new Date().toISOString())}.xlsx`);
  };

  const handleApplyFilters = () => setAppliedFilters({ ...tempFilters });

  const handleResetFilters = () => {
    const def = { dateRangeType: "today", customStartDate: "", customEndDate: "", campaignFilter: "", sourceFilter: "" };
    setTempFilters(def);
    setAppliedFilters(def);
  };

  return (
    <div className="w-full md:ml-[4.4rem] px-4 md:px-8 lg:px-10 mt-20 bg-main-bg min-h-screen pb-12">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=JetBrains+Mono:wght@400;500;600&display=swap');
        * { font-family: 'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif; }
        .mono { font-family: 'JetBrains Mono', monospace; }
        .stat-card { background: linear-gradient(135deg, #2563EB 0%, #1d4ed8 100%); position: relative; overflow: hidden; }
        .stat-card-success { background: linear-gradient(135deg, #10B981 0%, #059669 100%); }
        .stat-card-organic { background: linear-gradient(135deg, #8B5CF6 0%, #7C3AED 100%); }
        .stat-card-pid { background: linear-gradient(135deg, #F59E0B 0%, #D97706 100%); }
        .hover-lift:hover { transform: translateY(-2px); }
        .animate-spin { animation: spin 1s linear infinite; }
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        .download-menu {
          position: absolute; top: calc(100% + 8px); right: 0;
          background: white; border-radius: 16px;
          box-shadow: 0 20px 60px rgba(0,0,0,0.15);
          border: 1px solid #e5e7eb; z-index: 50; min-width: 200px; overflow: hidden;
        }
        .download-menu-item {
          display: flex; align-items: center; gap: 12px; padding: 14px 20px;
          cursor: pointer; font-weight: 600; font-size: 15px; transition: background 0.15s;
        }
        .download-menu-item:hover { background: #f1f5f9; }
        .download-menu-item.pdf { color: #dc2626; }
        .download-menu-item.excel { color: #16a34a; }
        .download-menu-item + .download-menu-item { border-top: 1px solid #f1f5f9; }
      `}</style>

      {/* HEADER */}
      <div className="mb-10">
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-end gap-6 mb-8">
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-main-blue rounded-xl shadow-lg">
                <BiTrendingUp className="text-2xl text-main-white" />
              </div>
              <h1 className="text-4xl lg:text-5xl font-bold text-main-font tracking-tight">
                Event Tracker Dashboard
              </h1>
            </div>
            <p className="text-main-subfont text-base lg:text-lg font-medium ml-16">
              Monitor and analyze your event tracking data
            </p>
          </div>

          <div className="flex items-center gap-3">
            {/* Download Button */}
            <div className="relative" ref={downloadMenuRef}>
              <button
                onClick={() => setShowDownloadMenu((prev) => !prev)}
                className="group bg-emerald-600 text-white px-7 py-4 rounded-2xl shadow-xl hover:shadow-2xl hover:bg-emerald-700 transition-all duration-300 flex items-center gap-3 font-semibold text-lg hover-lift"
              >
                <div className="p-2 bg-white/20 rounded-lg">
                  <FaDownload className="text-base" />
                </div>
                Download
                <FaChevronDown className={`text-sm transition-transform duration-200 ${showDownloadMenu ? "rotate-180" : ""}`} />
              </button>
              {showDownloadMenu && (
                <div className="download-menu">
                  <div className="download-menu-item pdf" onClick={handleDownloadPDF}>
                    <FaFilePdf className="text-lg" /> Download PDF
                  </div>
                  <div className="download-menu-item excel" onClick={handleDownloadExcel}>
                    <FaFileExcel className="text-lg" /> Download Excel
                  </div>
                </div>
              )}
            </div>

            {/* Refresh Button */}
            <button
              onClick={() => fetchDashboardData()}
              className="group bg-main-blue text-main-white px-8 py-4 rounded-2xl shadow-xl hover:shadow-2xl hover:bg-main-blue/90 transition-all duration-300 flex items-center gap-3 font-semibold text-lg hover-lift"
            >
              <div className="p-2 bg-main-white/20 rounded-lg group-hover:rotate-180 transition-transform duration-500">
                <MdRefresh className="text-base" />
              </div>
              Refresh
            </button>
          </div>
        </div>

        {/* STATS CARDS */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {[
            { label: "Total Events", value: summary.total, cls: "stat-card" },
            { label: "Success", value: summary.success, cls: "stat-card-success" },
            { label: "Organic", value: summary.organic, cls: "stat-card-organic" },
            { label: "PID", value: summary.pid, cls: "stat-card-pid" },
          ].map((card) => (
            <div key={card.label} className={`${card.cls} text-main-white p-6 rounded-2xl shadow-lg overflow-hidden relative`}>
              <div className="absolute top-0 right-0 w-32 h-32 bg-main-white/10 rounded-full -mr-8 -mt-8"></div>
              <div className="relative z-10">
                <p className="text-sm font-medium opacity-90 mb-1">{card.label}</p>
                <p className="text-4xl font-bold">{card.value || 0}</p>
              </div>
            </div>
          ))}
        </div>

        {/* FILTERS */}
        <div className="bg-main-white rounded-2xl shadow-lg border border-main-border p-6 mb-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 bg-main-blue/10 rounded-lg">
              <FaFilter className="text-main-blue text-lg" />
            </div>
            <h2 className="text-2xl font-bold text-main-font">Filters</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
            <div className="space-y-2">
              <label className="block text-sm font-bold text-main-font uppercase tracking-wide">📅 Date Range</label>
              <select
                value={tempFilters.dateRangeType}
                onChange={(e) => setTempFilters({ ...tempFilters, dateRangeType: e.target.value })}
                className="w-full px-4 py-3 border-2 border-main-border rounded-xl text-base font-semibold focus:outline-none focus:border-main-blue focus:ring-4 focus:ring-main-blue/10 transition-all duration-200"
              >
                <option value="today">Today</option>
                <option value="yesterday">Yesterday</option>
                <option value="last15">Last 15 Days</option>
                <option value="last30">Last 30 Days</option>
                <option value="last6months">Last 6 Months</option>
                <option value="custom">Custom Range</option>
              </select>
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-bold text-main-font uppercase tracking-wide">🎯 Campaign</label>
              <select
                value={tempFilters.campaignFilter}
                onChange={(e) => setTempFilters({ ...tempFilters, campaignFilter: e.target.value })}
                className="w-full px-4 py-3 border-2 border-main-border rounded-xl text-base font-semibold focus:outline-none focus:border-main-blue focus:ring-4 focus:ring-main-blue/10 transition-all duration-200"
              >
                <option value="">All Campaigns</option>
                {campaigns.map((camp) => (
                  <option key={camp} value={camp}>{camp}</option>
                ))}
              </select>
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-bold text-main-font uppercase tracking-wide">🔗 Source</label>
              <select
                value={tempFilters.sourceFilter}
                onChange={(e) => setTempFilters({ ...tempFilters, sourceFilter: e.target.value })}
                className="w-full px-4 py-3 border-2 border-main-border rounded-xl text-base font-semibold focus:outline-none focus:border-main-blue focus:ring-4 focus:ring-main-blue/10 transition-all duration-200"
              >
                <option value="">All Sources</option>
                <option value="organic">Organic</option>
                <option value="pid">PID</option>
              </select>
            </div>
          </div>

          {tempFilters.dateRangeType === "custom" && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6 p-4 bg-main-bg/50 rounded-xl border-2 border-main-border">
              <div className="space-y-2">
                <label className="block text-sm font-bold text-main-font uppercase tracking-wide">Start Date</label>
                <input type="date" value={tempFilters.customStartDate}
                  onChange={(e) => setTempFilters({ ...tempFilters, customStartDate: e.target.value })}
                  className="mono w-full px-4 py-3 border-2 border-main-border rounded-xl text-base focus:outline-none focus:border-main-blue focus:ring-4 focus:ring-main-blue/10 transition-all duration-200"
                />
              </div>
              <div className="space-y-2">
                <label className="block text-sm font-bold text-main-font uppercase tracking-wide">End Date</label>
                <input type="date" value={tempFilters.customEndDate}
                  onChange={(e) => setTempFilters({ ...tempFilters, customEndDate: e.target.value })}
                  className="mono w-full px-4 py-3 border-2 border-main-border rounded-xl text-base focus:outline-none focus:border-main-blue focus:ring-4 focus:ring-main-blue/10 transition-all duration-200"
                />
              </div>
            </div>
          )}

          <div className="flex flex-wrap gap-4">
            <button onClick={handleApplyFilters}
              className="bg-main-blue text-main-white px-8 py-3.5 rounded-2xl font-bold hover:bg-main-blue/90 shadow-lg hover:shadow-xl transition-all duration-300 flex items-center gap-2 hover-lift">
              <FaFilter /> Apply Filters
            </button>
            <button onClick={handleResetFilters}
              className="px-8 py-3.5 text-main-subfont font-bold hover:text-main-font hover:bg-main-white rounded-2xl transition-all duration-200 border-2 border-main-border flex items-center gap-2">
              <FaSync /> Reset
            </button>
          </div>
        </div>
      </div>

      {error && (
        <div className="mb-6 p-6 bg-red-50 border-2 border-red-200 rounded-2xl">
          <p className="text-red-700 font-semibold flex items-center gap-2">
            <span className="text-2xl">⚠️</span>{error}
          </p>
        </div>
      )}

      {/* ── PID TABLE ─────────────────────────────────────────────────────────── */}
      <div className="bg-main-white rounded-3xl shadow-2xl border border-main-border overflow-hidden mb-8">
        <div className="p-6 border-b-2 border-main-border flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div className="flex items-center gap-3">
            <div className="w-1 h-8 bg-main-blue rounded-full"></div>
            <h2 className="text-2xl font-bold text-main-font">PID – Event Summary</h2>
          </div>
          <span className="px-6 py-2 bg-main-blue/10 text-main-blue font-bold rounded-xl mono">
            {pidTableData.length} records
          </span>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-main-th border-b-2 border-main-border">
                <th className="p-5 text-left">
                  <div className="flex items-center gap-2">
                    <div className="w-1 h-6 bg-main-blue rounded-full"></div>
                    <span className="text-main-font font-bold text-sm uppercase tracking-wide">Campaign Name</span>
                  </div>
                </th>
                <th className="p-5 text-left">
                  <span className="text-main-font font-bold text-sm uppercase tracking-wide">PID</span>
                </th>
                <th className="p-5 text-center">
                  <span className="text-main-font font-bold text-sm uppercase tracking-wide">Event</span>
                </th>
                <th className="p-5 text-center">
                  <span className="text-main-font font-bold text-sm uppercase tracking-wide">Total Count</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="4" className="p-16">
                    <div className="flex flex-col items-center justify-center gap-4">
                      <span className="w-12 h-12 border-4 border-main-blue/30 border-t-main-blue rounded-full animate-spin"></span>
                      <p className="text-main-subfont font-medium animate-pulse">Loading data...</p>
                    </div>
                  </td>
                </tr>
              ) : pidTableData.length === 0 ? (
                <tr>
                  <td colSpan="4" className="p-16">
                    <div className="flex flex-col items-center gap-4">
                      <div className="p-6 bg-main-bg rounded-full">
                        <BiTrendingUp className="text-6xl text-main-subfont/30" />
                      </div>
                      <p className="text-main-font font-semibold text-xl">No PID data found</p>
                      <p className="text-main-subfont text-base">Try adjusting your filter criteria.</p>
                    </div>
                  </td>
                </tr>
              ) : (
                pidTableData.map((row, index) => (
                  <tr key={index} className="border-b border-main-border/50 hover:bg-main-thHover transition-all duration-200">
                    <td className="p-5">
                      <span className="text-main-font font-semibold text-base">{row.camp_name}</span>
                    </td>
                    <td className="p-5">
                      <span className="mono text-main-font font-semibold text-base px-3 py-1.5 bg-main-bg rounded-lg">
                        {row.p_id}
                      </span>
                    </td>
                    <td className="p-5 text-center">
                      <span className="px-4 py-2 bg-main-blue/10 text-main-blue font-semibold text-sm rounded-xl">
                        {row.event}
                      </span>
                    </td>
                    <td className="p-5 text-center">
                      <span className={`mono px-4 py-2 font-bold text-base rounded-xl shadow-md ${row.totalCount === 0 ? "bg-red-100 text-red-500" : "bg-main-blue text-main-white"
                        }`}>
                        {row.totalCount}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── ORGANIC TABLE ─────────────────────────────────────────────────────── */}
      <div className="bg-main-white rounded-3xl shadow-2xl border border-main-border overflow-hidden">
        <div className="p-6 border-b-2 border-main-border flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div className="flex items-center gap-3">
            <div className="w-1 h-8 bg-purple-500 rounded-full"></div>
            <h2 className="text-2xl font-bold text-main-font">Organic – Event Summary</h2>
          </div>
          <span className="px-6 py-2 bg-purple-100 text-purple-600 font-bold rounded-xl mono">
            {organicTableData.length} records
          </span>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-main-th border-b-2 border-main-border">
                <th className="p-5 text-left">
                  <div className="flex items-center gap-2">
                    <div className="w-1 h-6 bg-purple-500 rounded-full"></div>
                    <span className="text-main-font font-bold text-sm uppercase tracking-wide">Campaign Name</span>
                  </div>
                </th>
                <th className="p-5 text-left">
                  <span className="text-main-font font-bold text-sm uppercase tracking-wide">PID</span>
                </th>
                <th className="p-5 text-center">
                  <span className="text-main-font font-bold text-sm uppercase tracking-wide">Event</span>
                </th>
                <th className="p-5 text-center">
                  <span className="text-main-font font-bold text-sm uppercase tracking-wide">Total Count</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="4" className="p-16">
                    <div className="flex flex-col items-center justify-center gap-4">
                      <span className="w-12 h-12 border-4 border-purple-300 border-t-purple-500 rounded-full animate-spin"></span>
                      <p className="text-main-subfont font-medium animate-pulse">Loading data...</p>
                    </div>
                  </td>
                </tr>
              ) : organicTableData.length === 0 ? (
                <tr>
                  <td colSpan="4" className="p-16">
                    <div className="flex flex-col items-center gap-4">
                      <div className="p-6 bg-main-bg rounded-full">
                        <BiTrendingUp className="text-6xl text-main-subfont/30" />
                      </div>
                      <p className="text-main-font font-semibold text-xl">No organic events found</p>
                      <p className="text-main-subfont text-base">Try adjusting your filter criteria.</p>
                    </div>
                  </td>
                </tr>
              ) : (
                organicTableData.map((row, index) => (
                  <tr key={index} className="border-b border-main-border/50 hover:bg-main-thHover transition-all duration-200">
                    <td className="p-5">
                      <span className="text-main-font font-semibold text-base">{row.camp_name}</span>
                    </td>
                    <td className="p-5">
                      <span className="mono text-main-font font-semibold text-base px-3 py-1.5 bg-main-bg rounded-lg">
                        {row.p_id}
                      </span>
                    </td>
                    <td className="p-5 text-center">
                      <span className="px-4 py-2 bg-purple-100 text-purple-600 font-semibold text-sm rounded-xl">
                        {row.event}
                      </span>
                    </td>
                    <td className="p-5 text-center">
                      <span className="mono px-4 py-2 bg-purple-500 text-main-white font-bold text-base rounded-xl shadow-md">
                        {row.totalCount}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Home;